<template>
  <div class="h-full flex flex-col overflow-hidden text-sm select-none">

    <!-- Tab bar -->
    <div class="flex shrink-0 border-b border-gray-700 text-[11px]">
      <button
        v-for="tab in TABS" :key="tab"
        class="px-3 py-1.5 transition-colors"
        :class="activeTab === tab
          ? 'text-white border-b border-blue-500 -mb-px'
          : 'text-gray-500 hover:text-gray-300'"
        @click="activeTab = tab"
      >{{ tab }}</button>
    </div>

    <!-- Busy / error banner -->
    <div v-if="gitStore.busy" class="px-2 py-0.5 bg-gray-800 text-yellow-400 text-xs shrink-0">
      {{ gitStore.busyLabel }}
    </div>
    <div
      v-else-if="gitStore.error"
      class="px-2 py-0.5 bg-red-900/40 text-red-400 text-xs shrink-0 truncate cursor-help"
      :title="gitStore.error"
    >{{ gitStore.error }}</div>

    <!-- ── Changes tab ──────────────────────────────────────────────────────── -->
    <template v-if="activeTab === 'Changes'">

      <!-- Branch + sync toolbar -->
      <div class="flex items-center gap-1 px-2 py-1 border-b border-gray-700 shrink-0">
        <button
          class="flex items-center gap-1 text-blue-400 text-xs hover:text-white min-w-0 max-w-[120px]"
          title="Toggle branch list"
          @click="showBranches = !showBranches"
        >
          <span class="shrink-0">⎇</span>
          <span class="truncate">{{ gitStore.currentBranch }}</span>
        </button>
        <div class="flex-1" />
        <button
          class="text-gray-500 hover:text-white text-xs px-1 disabled:opacity-40"
          title="Pull"
          :disabled="gitStore.busy"
          @click="gitStore.pull()"
        >↓</button>
        <button
          class="text-gray-500 hover:text-white text-xs px-1 disabled:opacity-40"
          title="Push"
          :disabled="gitStore.busy"
          @click="gitStore.push()"
        >↑</button>
        <button
          class="text-gray-500 hover:text-white text-xs px-1 disabled:opacity-40"
          title="Refresh"
          :disabled="gitStore.loading"
          @click="refresh"
        >↺</button>
      </div>

      <!-- Branch manager dropdown -->
      <div v-if="showBranches" class="border-b border-gray-700 shrink-0 overflow-y-auto max-h-56">
        <BranchManager
          :branches="gitStore.branches"
          :current="gitStore.currentBranch"
          @checkout="handleCheckout"
          @create="handleCreateBranch"
        />
      </div>

      <!-- Loading skeleton -->
      <div v-if="gitStore.loading" class="flex-1 flex items-center justify-center text-gray-600 text-xs">
        Loading…
      </div>

      <template v-else>
        <div class="flex-1 overflow-y-auto min-h-0">

          <!-- Staged files -->
          <div v-if="staged.length">
            <div class="px-2 py-0.5 text-[10px] uppercase tracking-wider text-gray-500 flex items-center justify-between sticky top-0 bg-gray-900 z-10">
              Staged ({{ staged.length }})
              <button class="hover:text-gray-300 transition-colors" title="Unstage all" @click="gitStore.unstageAllFiles()">−all</button>
            </div>
            <GitFileRow
              v-for="f in staged" :key="'s:' + f.path"
              :file="f" mode="staged"
              @open="openFile(f.path)"
              @action="gitStore.unstage(f.path)"
              @diff="openDiff(f.path)"
            />
          </div>

          <!-- Unstaged files -->
          <div v-if="unstaged.length">
            <div class="px-2 py-0.5 text-[10px] uppercase tracking-wider text-gray-500 flex items-center justify-between sticky top-0 bg-gray-900 z-10">
              Changes ({{ unstaged.length }})
              <button class="hover:text-gray-300 transition-colors" title="Stage all" @click="gitStore.stageAllFiles()">+all</button>
            </div>
            <GitFileRow
              v-for="f in unstaged" :key="'u:' + f.path"
              :file="f" mode="unstaged"
              @open="openFile(f.path)"
              @action="gitStore.stage(f.path)"
              @diff="openDiff(f.path)"
            />
          </div>

          <div v-if="!staged.length && !unstaged.length" class="p-4 text-xs text-gray-600 text-center">
            Working tree clean
          </div>
        </div>

        <GitCommitForm
          v-model="commitMessage"
          :staged-count="staged.length"
          :total-count="staged.length + unstaged.length"
          :busy="gitStore.busy"
          @commit="commit"
          @commit-all="commitAll"
        />
      </template>
    </template>

    <!-- ── History tab ──────────────────────────────────────────────────────── -->
    <template v-else-if="activeTab === 'History'">
      <CommitList
        :commits="gitStore.commits"
        :loading="gitStore.loading"
        :has-more="hasMore"
        @select="onCommitSelect"
        @load-more="loadMore"
      />
    </template>

    <!-- ── Graph tab ────────────────────────────────────────────────────────── -->
    <template v-else-if="activeTab === 'Graph'">
      <GitGraph :commits="gitStore.commits" @select="onCommitSelect" />
    </template>

    <!-- ── Diff overlay ─────────────────────────────────────────────────────── -->
    <Teleport to="body">
      <div
        v-if="gitStore.diff"
        class="fixed inset-0 bg-black/75 z-50 flex flex-col"
        @click.self="gitStore.closeDiff()"
      >
        <div class="flex items-center gap-3 bg-gray-800 border-b border-gray-700 px-4 py-2 shrink-0">
          <span class="text-xs text-gray-400">Diff:</span>
          <span class="text-sm text-gray-100 font-mono truncate flex-1">{{ gitStore.diff.filePath }}</span>
          <button
            class="text-gray-400 hover:text-white text-lg leading-none ml-2"
            title="Close (Esc)"
            @click="gitStore.closeDiff()"
          >×</button>
        </div>
        <div class="flex-1 min-h-0">
          <DiffViewer
            :original="gitStore.diff.original"
            :modified="gitStore.diff.modified"
            :language="gitStore.diff.language"
          />
        </div>
      </div>
    </Teleport>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useEventListener } from '@vueuse/core'

import { useGitStore }    from '@/stores/gitStore'
import { useRepoStore }   from '@/stores/repoStore'
import { useEditorStore } from '@/stores/editorStore'

import BranchManager  from './BranchManager.vue'
import CommitList     from './CommitList.vue'
import GitGraph       from './GitGraph.vue'
import DiffViewer     from './DiffViewer.vue'
import GitFileRow     from './GitFileRow.vue'
import GitCommitForm  from './GitCommitForm.vue'

import type { GitCommit } from '@/services/gitService'

const gitStore    = useGitStore()
const repoStore   = useRepoStore()
const editorStore = useEditorStore()

const TABS = ['Changes', 'History', 'Graph'] as const
type Tab = typeof TABS[number]

const activeTab     = ref<Tab>('Changes')
const showBranches  = ref(false)
const commitMessage = ref('')
const historyLimit  = ref(50)

const staged   = computed(() => gitStore.status?.staged   ?? [])
const unstaged = computed(() => gitStore.status?.unstaged ?? [])
const repoPath = computed(() => repoStore.activeRepoPath)
const hasMore  = computed(() => gitStore.commits.length > 0 && gitStore.commits.length >= historyLimit.value)

// Close diff overlay with Escape
useEventListener('keydown', (e: KeyboardEvent) => {
  if (e.key === 'Escape' && gitStore.diff) gitStore.closeDiff()
})

onMounted(async () => {
  if (!repoPath.value) return
  await gitStore.fetchStatus(repoPath.value)
  await gitStore.fetchBranches(repoPath.value)
})

// Reload when active repo changes
watch(repoPath, async (path) => {
  if (!path) return
  historyLimit.value = 50
  await gitStore.fetchStatus(path)
  await gitStore.fetchBranches(path)
})

// Always reload history data when switching to History/Graph tabs
watch(activeTab, async (tab) => {
  if ((tab === 'History' || tab === 'Graph') && repoPath.value) {
    await gitStore.fetchLog(repoPath.value, historyLimit.value)
  }
})

async function refresh(): Promise<void> {
  if (!repoPath.value) return
  await gitStore.fetchStatus(repoPath.value)
  await gitStore.fetchBranches(repoPath.value)
  if (activeTab.value === 'History' || activeTab.value === 'Graph') {
    await gitStore.fetchLog(repoPath.value, historyLimit.value)
  }
}

async function loadMore(): Promise<void> {
  if (!repoPath.value) return
  historyLimit.value += 50
  await gitStore.fetchLog(repoPath.value, historyLimit.value)
}

function openFile(filePath: string): void {
  editorStore.openFile(filePath)
}

async function openDiff(filePath: string): Promise<void> {
  if (!repoPath.value) return
  const repoName = repoStore.activeRepo?.name ?? ''
  const wsPath   = repoName ? `${repoName}/${filePath}` : filePath
  await gitStore.openDiff(repoPath.value, filePath, wsPath)
}

async function commit(): Promise<void> {
  if (!commitMessage.value.trim() || !staged.value.length) return
  await gitStore.commit(commitMessage.value.trim())
  commitMessage.value = ''
}

async function commitAll(): Promise<void> {
  if (!commitMessage.value.trim()) return
  await gitStore.stageAllFiles()
  if (gitStore.error) return   // stage failed — don't commit
  await gitStore.commit(commitMessage.value.trim())
  commitMessage.value = ''
}

async function handleCheckout(branch: string): Promise<void> {
  await gitStore.checkout(branch)
  showBranches.value = false
}

async function handleCreateBranch(name: string): Promise<void> {
  await gitStore.createNewBranch(name)
  showBranches.value = false
}

function onCommitSelect(_commit: GitCommit): void {
  // future: show commit detail panel
}
</script>
